import React, { useState } from "react";
import { Clock, Search, Home } from "lucide-react";
import { Link } from "react-router-dom";

import blogimage1 from "../../assests/images/jmx_blog.png";
import blogimage2 from "../../assests/images/blogimage2.webp";
import blogimage3 from "../../assests/images/kafka_Replay.png";
import blogimage4 from "../../assests/images/latency.png";
import blogimage5 from "../../assests/images/iceberg.png";

const BlogPosts = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const blogs = [
    {
      title: "Apache Iceberg Hands-On: Understanding Iceberg Manifests",
      description:
        "A hands-on guide to understanding Apache Iceberg manifests and their role in tracking table data and metadata.",
      date: "2026-09-19",
      readTime: "5 min",
      platform: "medium",
      link: "https://medium.com/@kbnagaraj18/apache-iceberg-hands-on-understanding-iceberg-manifests-73f72ddf59d4?sharedUserId=kbnagaraj18",
      image: blogimage5,
      tags: ["Apache Iceberg", "Data Engineering", "Data Lake"],
    },
    {
      title: "Apache Iceberg Hands-On: Creating Our First Iceberg Table",
      description:
        "A hands-on guide to creating an Apache Iceberg table and exploring the foundations of Iceberg table management.",
      date: "2026-09-14",
      readTime: "6 min",
      platform: "medium",
      link: "https://medium.com/@kbnagaraj18/apache-iceberg-hands-on-creating-our-first-iceberg-table-4bfd7d5ba1c6?sharedUserId=kbnagaraj18",
      image: blogimage5,
      tags: ["Apache Iceberg", "PySpark", "Data Engineering", "Data Lake"],
    },
    {
      title:
        "Apache Iceberg Hands-On: Setting Up PySpark & JupyterLab on Windows",
      description:
        "A hands-on guide to setting up Apache Iceberg with PySpark and JupyterLab on Windows, exploring modern data lake workflows and Iceberg fundamentals.",
      date: "2026-09-13",
      readTime: "3 min",
      platform: "medium",
      link: "https://medium.com/@kbnagaraj18/apache-iceberg-hands-on-setting-up-pyspark-jupyterlab-on-windows-aec016c1690a?sharedUserId=kbnagaraj18",
      image: blogimage5,
      tags: [
        "Apache Iceberg",
        "PySpark",
        "JupyterLab",
        "Data Engineering",
        "Data Lake",
      ],
    },

    {
      title:
        "LatencyWatch : Trace Nested Function Latency in Python with Just One Decorator",
      description:
        "Building things is easy. Maintaining and scaling them is hard. When you’re debugging a slow service...",
      date: "2025-06-22",
      readTime: "4 min",
      platform: "medium",
      link: "https://medium.com/@kbnagaraj18/latencywatch-trace-nested-function-latency-in-python-with-just-one-decorator-a13fa473bcdd",
      image: blogimage4,
      tags: [
        "Python",
        "Latency",
        "Profilier",
        "Developer Tools",
        "Performance",
      ],
    },

    {
      title: "Going Back in Time with Kafka: Understanding Replay",
      description:
        "When working with Kafka, one of the most underrated but incredibly powerful features is the ability to replay messages.....",
      date: "2025-03-24",
      readTime: "4 min",
      platform: "medium",
      link: "https://medium.com/@kbnagaraj18/going-back-in-time-with-kafka-understanding-replay-6c9d9cf54983",
      image: blogimage3,
      tags: ["Apache Kafka", "Kafka-consumer", "Kafka-producer"],
    },

    {
      title: "Managing Kafka Offsets in PySpark Batch Jobs with MongoDB",
      description:
        "When working with PySpark, I initially explored structured streaming, which automatically handles Kafka .....",
      date: "2025-03-08",
      readTime: "3 min",
      platform: "medium",
      link: "https://medium.com/@kbnagaraj18/managing-kafka-offsets-in-pyspark-batch-jobs-with-mongodb-9beb14788039",
      image: blogimage2,
      tags: ["Apache Kafka", "PySpark", "Batch Processing"],
    },

    {
      title: "Exposing Kafka Cluster Metrics Using JMX: A Practical Guide",
      description:
        "In today’s fast-paced digital ecosystem, metrics are the foundation of reliable and efficient systems....",
      date: "2024-12-30",
      readTime: "5 min",
      platform: "medium",
      link: "https://medium.com/@kbnagaraj18/exposing-kafka-cluster-metrics-using-jmx-a-practical-guide-85fb4fae469f",
      image: blogimage1,
      tags: ["Apache Kafka", "JMX", "AWS Cloudwatch", "MBeans", "Collectd"],
    },
  ];

  // Sort blogs by date - newest first
  const sortedBlogs = [...blogs].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  // Filter and search
  const filteredBlogs = sortedBlogs
    .filter((blog) => filter === "all" || blog.platform === filter)
    .filter((blog) =>
      blog.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Pagination
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);

  const paginatedBlogs = filteredBlogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Search handler
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Filter handler
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCurrentPage(1);
  };

  // Items per page handler
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  // Pagination handler
  const handlePageChange = (direction) => {
    setCurrentPage((prev) =>
      Math.max(1, Math.min(totalPages, prev + direction))
    );
  };

  return (
    <section className="px-4 pt-6 md:pt-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0 mb-8 md:mb-12">
        <Link
          to="/"
          className="flex items-center gap-2 px-3 md:px-4 py-2 bg-black/30 border-2
                     border-cyan-400 rounded-lg text-cyan-400 text-sm md:text-base
                     hover:border-[#ff00ff] hover:text-[#ff00ff]
                     transition-colors"
        >
          <Home size={18} className="md:w-5 md:h-5" />
          <span>Home</span>
        </Link>

        <h2 className="font-mono text-2xl md:text-3xl order-first md:order-none">
          <span className="text-green-500">&lt;</span>
          <span className="text-[#ff00ff]">BLOG_POSTS</span>
          <span className="text-green-500">/&gt;</span>
        </h2>

        <div className="hidden md:block w-[88px]"></div>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-6 md:mb-8">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-400"
            size={18}
          />

          <input
            type="text"
            placeholder="Search blogs..."
            className="w-full bg-black/30 border-2 border-cyan-400 rounded-lg
                       pl-10 pr-4 py-2 text-sm md:text-base text-green-500
                       placeholder-green-500/50 focus:border-[#ff00ff]
                       focus:outline-none transition-colors"
            onChange={handleSearchChange}
          />
        </div>

        <select
          className="bg-black/30 border-2 border-cyan-400 rounded-lg px-4 py-2
                     text-sm md:text-base text-green-500 focus:border-[#ff00ff]
                     focus:outline-none transition-colors"
          onChange={handleFilterChange}
        >
          <option value="all">All Posts</option>
          <option value="medium">Medium</option>
        </select>
      </div>

      {/* Blog Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {paginatedBlogs.map((blog) => (
          <a
            key={blog.link}
            href={blog.link}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block h-full"
          >
            <div
              className="absolute -inset-0.5 bg-gradient-to-r from-cyan-400
                         via-[#ff00ff] to-cyan-400 rounded-lg blur opacity-20
                         group-hover:opacity-40 transition duration-300"
            />

            <div
              className="relative flex h-full flex-col bg-black border-2
                         border-cyan-400 rounded-lg overflow-hidden
                         group-hover:border-[#ff00ff] transition-colors"
            >
              {/* Blog Image */}
              <div className="aspect-video w-full overflow-hidden">
                <img
                  src={blog.image}
                  alt={`Blog cover for ${blog.title}`}
                  className="w-full h-full object-cover transition-transform
                             duration-300 group-hover:scale-105 filter
                             saturate-50 group-hover:saturate-100"
                />
              </div>

              {/* Blog Content */}
              <div className="flex flex-1 flex-col p-4 md:p-6">
                <div
                  className="flex items-center gap-4 text-xs md:text-sm
                             text-cyan-400 mb-2 md:mb-3"
                >
                  {blog.readTime && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} className="md:w-4 md:h-4" />
                      {blog.readTime}
                    </span>
                  )}

                  <span>{blog.platform}</span>

                  <span>
                    {new Date(blog.date).toLocaleDateString("en-GB")}
                  </span>
                </div>

                <h3 className="text-lg md:text-xl font-mono text-[#ff00ff] mb-2">
                  {blog.title}
                </h3>

                <p className="flex-1 text-xs md:text-sm text-green-500 mb-4">
                  {blog.description}
                </p>

                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {blog.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-0.5 md:py-1 border
                                 border-cyan-400 rounded text-cyan-400
                                 group-hover:border-[#ff00ff]
                                 group-hover:text-[#ff00ff]
                                 transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* Pagination */}
      <div
        className="flex flex-col md:flex-row justify-between items-center
                   gap-4 mt-6 md:mt-8 text-cyan-400"
      >
        <select
          className="w-full md:w-auto bg-black/30 border-2 text-green-500
                     text-sm md:text-base rounded-lg px-4 py-2
                     focus:border-[#ff00ff] focus:outline-none
                     transition-colors"
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
        >
          <option value="6">6 per page</option>
          <option value="12">12 per page</option>
        </select>

        <div className="flex gap-2">
          <button
            onClick={() => handlePageChange(-1)}
            disabled={currentPage === 1}
            className="px-3 md:px-4 py-2 text-sm md:text-base border-2
                       border-cyan-400 rounded-lg hover:border-[#33ff00]
                       hover:text-[#ff00ff] transition-colors
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            &lt;
          </button>

          <span
            className="px-3 md:px-4 py-2 text-sm md:text-base border-2
                       border-[#ff00ff] rounded-lg bg-[#ff00ff]/10"
          >
            {currentPage} / {totalPages}
          </span>

          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === totalPages}
            className="px-3 md:px-4 py-2 text-sm md:text-base border-2
                       border-cyan-400 rounded-lg hover:border-[#ff00ff]
                       hover:text-[#ff00ff] transition-colors
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            &gt;
          </button>
        </div>
      </div>
    </section>
  );
};

export default BlogPosts;